import React from 'react';
import { styled } from '@this/constants/themes';
import _ from 'lodash';

import type ReserveInfo from '@this/domain/reserve_info2';
import type OrderItemMapping from '@this/domain/order_item_mapping2';
import type { OrderItemMappingMode } from '@this/domain/order_item_mapping2';

type Props = {
  reserveInfo: ReserveInfo;
  mappingList: OrderItemMapping[];
  mode: OrderItemMappingMode;
};

const OrderItemMappingListSection: React.FC<Props> = ({ reserveInfo, mappingList, mode }) => {
  return (
    <ChildContent>
      {mode === 'item' ? (
        <TravelerBox>
          <MappingTitle>商品ごと</MappingTitle>
          {reserveInfo.reservingTrip.isPackage ? (
            <MappingBox>
              <MappingLabel>パッケージ</MappingLabel>
              <MappingText>
                {mappingList.find(m => m.travelerId === -1 && m.orderItemType === 'package')?.selectedOptionLabel}
              </MappingText>
            </MappingBox>
          ) : (
            <>
              {reserveInfo.itemList.map((item, i) => (
                <MappingBox key={i}>
                  {item.elementType === 'hotel' ? (
                    <MappingLabel>{i + 1}. ホテル</MappingLabel>
                  ) : (
                    <MappingLabel>{i + 1}. 経路</MappingLabel>
                  )}
                  <MappingText>
                    {
                      mappingList.find(
                        m => m.travelerId === -1 && m.orderItemType === 'item' && m.orderItemIndex === i
                      )?.selectedOptionLabel
                    }
                  </MappingText>
                </MappingBox>
              ))}
            </>
          )}
          {reserveInfo.rentalCarRequired === 'true' && (
            <>
              {reserveInfo.reservingTrip.rentalCars.map((_r, i) => (
                <MappingBox key={i}>
                  <MappingLabel>{`レンタカー${i + 1}`}</MappingLabel>
                  <MappingText>
                    {
                      mappingList.find(
                        m => m.travelerId === -1 && m.orderItemType === 'rental_car' && m.orderItemIndex === i
                      )?.selectedOptionLabel
                    }
                  </MappingText>
                </MappingBox>
              ))}
            </>
          )}
        </TravelerBox>
      ) : mode === 'traveler' ? (
        <TravelerBox>
          <MappingTitle>出張者ごと</MappingTitle>
          {reserveInfo.reservingTrip.travelers.map((traveler, i) => (
            <MappingBox key={i}>
              <MappingLabel>{traveler.travelerName}</MappingLabel>
              <MappingText>
                {mappingList.find(m => m.travelerId === i && m.orderItemType === 'all')?.selectedOptionLabel}
              </MappingText>
            </MappingBox>
          ))}
        </TravelerBox>
      ) : mode === 'both' ? (
        <TravelerBox>
          <MappingTitle>商品×出張者ごと</MappingTitle>
          {reserveInfo.reservingTrip.isPackage ? (
            <MappingBox>
              <MappingLabel>パッケージ</MappingLabel>
              {reserveInfo.reservingTrip.travelers.map((traveler, i) => (
                <React.Fragment key={i}>
                  <MappingLabel>{traveler.travelerName}</MappingLabel>
                  <MappingText>
                    {
                      mappingList.find(m => m.travelerId === i && m.orderItemType === 'package')
                        ?.selectedOptionLabel
                    }
                  </MappingText>
                </React.Fragment>
              ))}
            </MappingBox>
          ) : (
            <>
              {reserveInfo.itemList.map((item, i) => (
                <MappingBox key={i}>
                  {item.elementType === 'hotel' ? (
                    <MappingLabel>{i + 1}. ホテル</MappingLabel>
                  ) : (
                    <MappingLabel>{i + 1}. 経路</MappingLabel>
                  )}
                  {reserveInfo.reservingTrip.travelers.map((traveler, i) => (
                    <React.Fragment key={i}>
                      <MappingLabel>{traveler.travelerName}</MappingLabel>
                      <MappingText>
                        {
                          mappingList.find(m => m.travelerId === i && m.orderItemType === 'item')
                            ?.selectedOptionLabel
                        }
                      </MappingText>
                    </React.Fragment>
                  ))}
                </MappingBox>
              ))}
            </>
          )}
          {reserveInfo.rentalCarRequired === 'true' && (
            <>
              {reserveInfo.reservingTrip.rentalCars.map((_r, i) => (
                <MappingBox key={i}>
                  <MappingLabel>{`レンタカー${i + 1}`}</MappingLabel>
                  {reserveInfo.reservingTrip.travelers.map((traveler, j) => (
                    <React.Fragment key={j}>
                      <MappingLabel>{traveler.travelerName}</MappingLabel>
                      <MappingText>
                        {
                          mappingList.find(
                            m => m.travelerId === i && m.orderItemType === 'rental_car' && m.orderItemIndex === i
                          )?.selectedOptionLabel
                        }
                      </MappingText>
                    </React.Fragment>
                  ))}
                </MappingBox>
              ))}
            </>
          )}
        </TravelerBox>
      ) : null}
    </ChildContent>
  );
};

const ChildContent = styled.div`
  font-size: 13px;
  margin-left: 10px;
`;

const MappingBox = styled.div`
  width: 100%;
  display: flex;
`;

const MappingTitle = styled.p`
  margin: 10px 5px 5px;
`;

const MappingLabel = styled.div`
  line-height: 39px;
  padding-right: 5px;
`;

const MappingText = styled.div`
  line-height: 39px;
`;

const TravelerBox = styled.div`
  margin-bottom: 15px;
  margin-left: 10px;
`;

export default OrderItemMappingListSection;
